import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { Router } from '@angular/router';
import * as fromRoot from '@app/reducers';
import { addOrUpdateUrlParam } from '@app/shared/helpers/urlHelper';
import { select, Store } from '@ngrx/store';
import { Observable, of, ReplaySubject } from 'rxjs';
import { mergeMap, take, takeUntil, withLatestFrom } from 'rxjs/operators';
import { Constant } from 'src/constant';
import { TokenService } from '../services/token.service';
import { IsAccountValidGuard } from './base/is-account-valid.guard';
import { LoadDomainGuard } from './base/load-domain.guard';
import { LoadServicesGuard } from './base/load-services.guard';
import { isPlatformServer } from '@angular/common';
import { SEOService } from '../services/seo.service';
import { isAppDomain, getProtocolHostnameAndPort } from '@app/shared/helpers/location';
import { encodeSamlRequest } from 'smash-saml';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root'
})
export class SigninCanActivateGuard {
  constructor(
    private loadDomainGuard: LoadDomainGuard,
    private loadServicesGuard: LoadServicesGuard,
    private store$: Store<fromRoot.State>,
    private tokenService: TokenService,
    private isAccountValid: IsAccountValidGuard,
    private router: Router,
    private seoService: SEOService,
    @Inject(PLATFORM_ID) private platformId: object,
  ) { }

  canActivate(): Observable<boolean> {
    if (isPlatformServer(this.platformId)) {
      this.seoService.setMetasSignin();
      return of(true);
    }
    return this.loadDomainGuard.canActivate().pipe(mergeMap((domainLoaded: boolean) => {
      if (domainLoaded) {
        return this.loadServicesGuard.canActivate().pipe(mergeMap((servicesLoaded: boolean) => {
          if (servicesLoaded) {
            return this.isAccountValid.canActivate().pipe(mergeMap((valid: boolean) => {
              if (valid) {
                return this.isNotLoggedIn().pipe(mergeMap(isNotLoggedIn => {
                  if (isNotLoggedIn) {
                    return this.chechRedirectionFromDomainOptions().pipe(mergeMap(stayOnSignin => {
                      return of(stayOnSignin);
                    }));
                  } else {
                    this.router.navigateByUrl('/');
                    return of(false);
                  }
                }));
              }
            }));
          }
        }));
      }
    }));
  }

  isNotLoggedIn(): Observable<boolean> {
    const isNotLoggedIn$ = new ReplaySubject<boolean>();
    this.store$.pipe(
      select(fromRoot.getToken),
      takeUntil(isNotLoggedIn$)
    ).subscribe((token) => {
      if (token) {
        if (this.tokenService.isAnonymousAccount(token)) {
          isNotLoggedIn$.next(true);
        } else {
          if (this.tokenService.isTokenExpired(token)) {
            isNotLoggedIn$.next(true);
          } else {
            isNotLoggedIn$.next(false);
          }
        }
      } else {
        isNotLoggedIn$.next(true);
      }
    });
    return isNotLoggedIn$.asObservable();
  }

  chechRedirectionFromDomainOptions(): Observable<boolean> {
    const stayHere$ = new ReplaySubject<boolean>();
    this.store$.pipe(
      withLatestFrom(
        this.store$.select(fromRoot.getCurrentDomain),
        this.store$.select(fromRoot.selectServices),
        this.store$.select(fromRoot.getCurrentDomainProviders),
      ),
      take(1),
    ).subscribe(([state, currentDomain, { services }, providers]) => {
      if (currentDomain?.options?.redirection?.value) {
        const redirectTo = currentDomain.options.redirection.value;
        switch (redirectTo) {
          case Constant.domainLoginRedirection.None:
            stayHere$.next(true);
            break;
          case Constant.domainLoginRedirection.Signin:
            stayHere$.next(true);
            break;
          case Constant.domainLoginRedirection.Provider:
            if (providers?.length > 0) {
              const idpUrl = providers[0].idpUrl;
              const audienceUris = providers[0].audienceUris;
              const consumerServiceUrl = services.find(service => service.name === 'iam' && service.region === currentDomain.region).url + '/saml/signon';
              const correspondingAudienceUri = isAppDomain() ? environment.appDomainWithProtocol : audienceUris.find(audienceUri => audienceUri === getProtocolHostnameAndPort());
              const SAMLRequest = encodeSamlRequest({ consumerServiceUrl, audienceUri: correspondingAudienceUri });
              window.location.href = addOrUpdateUrlParam(idpUrl, Constant.SAMLRequestKey, SAMLRequest);
              stayHere$.next(false);
            } else {
              stayHere$.next(true);
            }
            break;
          default:
            stayHere$.next(true);
        }
      } else {
        stayHere$.next(true);
      }
    });
    return stayHere$.asObservable();
  }
}
